"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Panel,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, WIDGET_H2 } from "@/components/tool-kit/styles";
import { CheckList } from "@/components/tools/research-writing/check-list";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  buildDiscussionOutline,
  type DiscussionInput,
  discussionChecks,
  discussionCounts,
  STUDY_TYPES,
} from "@/lib/tool-kit/research-writing/discussion-outline";

/** English strings. A translated content file overrides these through `ui`. */
export const DISCUSSION_SECTION_OUTLINE_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  studyHeading: "Your study",
  titleLabel: "Working title",
  titlePlaceholder: "e.g. Discussion — Peer Tutoring and Re-enrollment",
  wordsLabel: "Target length (words, optional)",
  wordsPlaceholder: "e.g. 1500",
  wordsHint: "Adds a word target to each part of the discussion.",
  aimLabel: "Aim or question of the study",
  aimPlaceholder: "What the study set out to find out",
  studyTypeLabel: "Type of study",
  studyTypes: {
    quantitative: "Quantitative",
    qualitative: "Qualitative",
    other: "Other or mixed",
  },
  findingsHeading: "Findings and earlier research",
  findingsLabel: "Principal findings — one per line",
  findingsPlaceholder:
    "The main result, in one sentence\nA second result, if it matters as much",
  comparisonLabel:
    "Earlier studies — one per line, “Study: how yours compares”",
  comparisonPlaceholder:
    "Author, year: consistent, though the outcome was course grades\nAuthor, year: smaller effect, different sample",
  interpretationLabel: "What might explain the findings?",
  interpretationPlaceholder:
    "Possible mechanisms, and rival explanations you cannot rule out",
  meaningHeading: "Meaning and limits",
  practiceLabel: "Implications for practice or policy",
  practicePlaceholder:
    "What practitioners or policymakers might do differently",
  theoryLabel: "Implications for theory",
  theoryPlaceholder:
    "Whether the findings support, qualify or challenge a theory",
  limitationsLabel: "Limitations — one per line",
  limitationsPlaceholder:
    "Observational design: students chose whether to attend\nOne institution",
  futureLabel: "Unanswered questions and future research — one per line",
  futurePlaceholder:
    "Does a randomized offer of tutoring change re-enrollment?",
  closingLabel: "Closing message",
  closingPlaceholder: "The take-home message, with its main caveat",
  resultsHeading: "Checklist",
  statWords: "Words written",
  statFindings: "Findings",
  statComparisons: "Earlier studies",
  statLimitations: "Limitations",
  checksLabel: "Checklist",
  checkStates: { done: "Done", todo: "To do", note: "Check" },
  checkText: {
    findings_done: "Principal findings are stated.",
    findings_todo: "State the principal findings first.",
    comparison_done: "Findings are compared with earlier research.",
    comparison_todo: "Compare your findings with earlier studies.",
    interpretation_done: "Possible explanations are offered.",
    interpretation_todo: "Offer an explanation, and a rival one.",
    implications_done: "Implications are drawn.",
    implications_todo:
      "Say what the findings mean for practice, policy or theory.",
    limitations_done: "Limitations are named.",
    limitations_todo:
      "Name the limitations. Readers care about them, and an unmentioned flaw costs trust.",
    future_done: "Future research is suggested.",
    future_todo: "Say what is still unanswered.",
    closing_done: "There is a closing message.",
    closing_todo: "Close with a message the reader can carry away.",
    overclaim_done: "No words that assert proof.",
    overclaim_note:
      "Found: {items}. A single study rarely proves anything: consider “suggests” or “is consistent with”. This is a short word list, not a judgement.",
  },
  fileName: "discussion-section-outline",
  document: {
    heading: "Your discussion outline",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Every part has your content.",
  },
  defaultTitle: "Discussion",
  subtitle: "Discussion section outline",
  subtitleWords: "Discussion section outline · {words} words",
  headings: {
    findings: "1. Principal findings",
    comparison: "2. Comparison with earlier research",
    interpretation: "3. Interpretation",
    implications: "4. Implications",
    limitations: "5. Limitations",
    future: "6. Unanswered questions and future research",
    closing: "7. Closing message",
    budget: "Word Budget",
  },
  target: "Target: about {words} words.",
  tips: {
    findings:
      "Open with the main result, in a sentence, without repeating the Results section.",
    comparison:
      "Set your results against earlier studies and spend the most time where they differ. Explain differences by sample, method, measure or setting.",
    interpretation:
      "Offer possible explanations and a rival one. Where the design cannot show cause, write “suggests” or “is consistent with”.",
    implications:
      "Say what the findings mean for practice, policy and theory, and go no further than the evidence does.",
    limitations: "Name the main limitations of the study.",
    future:
      "Turn the limits and open questions into specific studies someone could run.",
    closing:
      "End with a message the reader can carry away, and the caveat it needs.",
  },
  tipsLimitations: {
    quantitative:
      "Name the limits of design, sample and measures, and say which way each could bias the result and by how much (STROBE asks for direction and magnitude of bias). Say how far the results apply beyond your sample.",
    qualitative:
      "Name the limits of your sample, your role as the researcher and your analysis, and how you addressed them. Qualitative findings are judged for transferability — whom they might apply to — not statistical generalization.",
    other:
      "Name the limits of your method, evidence and scope, and how they affect what you can claim.",
  },
  aimFieldLabel: "Aim of the study",
  aimPrompt: "Restate the aim or question of the study in one sentence.",
  prompts: {
    findings:
      "State the principal finding in one sentence: “We found that [result], [with its size or direction].”",
    comparison:
      "For each relevant earlier study: “[Author, year] found [result]; ours is [consistent with / larger than / different from] it.”",
    differ:
      "Where your results differ from earlier work, say why: a different sample, method, measure or setting.",
    interpretation:
      "Offer an explanation: “One possible explanation is [mechanism]. An alternative is [rival explanation], which our design [can / cannot] rule out.”",
    practice:
      "Say what practitioners or policymakers might do differently, and how far the evidence supports it.",
    theory:
      "Say how the findings support, qualify or challenge a theory or earlier model.",
    limitations:
      "List the main limitations of design, sample and measures, with the likely direction of bias: “Because [feature], [result] may be [over/under]estimated.”",
    future:
      "Name specific studies the work points to: “Future research should test whether [question], using [design].”",
    closing: "Close with the take-home message and its main caveat.",
  },
  practiceHeading: "For practice or policy",
  theoryHeading: "For theory",
  comparisonHead: {
    study: "Earlier study",
    comparison: "How your finding compares",
  },
  budgetHead: { part: "Part", words: "Target words" },
  budgetTotal: "Total",
  example: {
    title: "Discussion — Peer Tutoring and Re-enrollment",
    words: "1500",
    aim: "To find out whether attending peer tutoring in the first semester is associated with re-enrollment in the second year, and for whom.",
    findings:
      "Students who attended peer tutoring were more likely to re-enroll (adjusted odds ratio [value]).\nThe association was [stronger / weaker / similar] for first-generation students.\nInterviewed students described tutoring as [theme from the interviews].",
    comparison:
      "[Author A, year]: consistent in direction, though their outcome was course grades, not re-enrollment\n[Author B, year]: a smaller association, in a sample of [describe]",
    interpretation:
      "One possible explanation is that tutoring strengthens students’ sense of belonging, which is consistent with [theory or study]. An alternative is that students who seek tutoring were already more motivated, which adjusting for prior grades addresses only in part.",
    practice:
      "If the pattern holds elsewhere, directing tutoring to first-generation students in gateway courses may retain more students than offering it to everyone.",
    theory:
      "The findings are consistent with belonging-based accounts of retention [ref], but they do not test them directly.",
    limitations:
      "Observational design: students chose whether to attend, so selection may explain part of the association.\nOne institution: the results may not transfer to other settings.\nAttendance was recorded, but the content and quality of sessions were not.",
    future:
      "Does a randomized offer of tutoring change re-enrollment?\nWhich features of tutoring — training, frequency, format — matter most?",
    closing:
      "Peer tutoring was associated with staying enrolled, particularly for first-generation students, but a randomized test is needed before treating the association as a cause.",
  },
} as const;

const EMPTY: DiscussionInput = {
  title: "",
  aim: "",
  studyType: "quantitative",
  findings: "",
  comparison: "",
  interpretation: "",
  practice: "",
  theory: "",
  limitations: "",
  future: "",
  closing: "",
  words: "",
};

type TextKey = Exclude<keyof DiscussionInput, "studyType">;

export default function DiscussionSectionOutlineWidget() {
  const strings = useToolUi(DISCUSSION_SECTION_OUTLINE_STRINGS);
  const fmt = useToolNumber();
  const [input, setInput] = useState<DiscussionInput>(EMPTY);
  const labels = useMemo(
    () => ({ ...strings, aimLabel: strings.aimFieldLabel }),
    [strings]
  );
  const doc = useMemo(
    () => buildDiscussionOutline(input, labels, fmt),
    [input, labels, fmt]
  );
  const documentLabels = useMemo(() => strings.document, [strings]);
  const counts = discussionCounts(input);
  const checkText = strings.checkText as Record<string, string>;
  const checks = discussionChecks(input).map((check) => ({
    key: check.id,
    state: check.state,
    text: t(checkText[`${check.id}_${check.state}`] ?? "", {
      items: (check.items ?? []).join(", "),
    }),
  }));
  const update = (key: TextKey) => (value: string) =>
    setInput((previous) => ({ ...previous, [key]: value }));
  const area = (
    key: TextKey,
    id: string,
    label: string,
    placeholder: string,
    rows = 3
  ) => (
    <TextAreaField
      id={`ds-${id}`}
      label={label}
      onChange={update(key)}
      placeholder={placeholder}
      prose
      rows={rows}
      value={input[key]}
    />
  );

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() =>
            setInput({
              ...EMPTY,
              ...strings.example,
              studyType: "quantitative",
            })
          }
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput(EMPTY)}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <Panel title={strings.studyHeading}>
        <div className="grid gap-4 sm:grid-cols-[1fr_12rem]">
          <TextField
            id="ds-title"
            label={strings.titleLabel}
            onChange={update("title")}
            placeholder={strings.titlePlaceholder}
            value={input.title}
          />
          <TextField
            hint={strings.wordsHint}
            id="ds-words"
            label={strings.wordsLabel}
            numeric
            onChange={update("words")}
            placeholder={strings.wordsPlaceholder}
            value={input.words}
          />
        </div>
        {area("aim", "aim", strings.aimLabel, strings.aimPlaceholder, 2)}
        <SelectField
          id="ds-type"
          label={strings.studyTypeLabel}
          onChange={(studyType) =>
            setInput((previous) => ({ ...previous, studyType }))
          }
          options={STUDY_TYPES.map((value) => ({
            value,
            label: strings.studyTypes[value],
          }))}
          value={input.studyType}
        />
      </Panel>

      <Panel title={strings.findingsHeading}>
        {area(
          "findings",
          "findings",
          strings.findingsLabel,
          strings.findingsPlaceholder
        )}
        {area(
          "comparison",
          "comparison",
          strings.comparisonLabel,
          strings.comparisonPlaceholder
        )}
        {area(
          "interpretation",
          "interpretation",
          strings.interpretationLabel,
          strings.interpretationPlaceholder
        )}
      </Panel>

      <Panel title={strings.meaningHeading}>
        {area(
          "practice",
          "practice",
          strings.practiceLabel,
          strings.practicePlaceholder,
          2
        )}
        {area(
          "theory",
          "theory",
          strings.theoryLabel,
          strings.theoryPlaceholder,
          2
        )}
        {area(
          "limitations",
          "limitations",
          strings.limitationsLabel,
          strings.limitationsPlaceholder
        )}
        {area(
          "future",
          "future",
          strings.futureLabel,
          strings.futurePlaceholder,
          2
        )}
        {area(
          "closing",
          "closing",
          strings.closingLabel,
          strings.closingPlaceholder,
          2
        )}
      </Panel>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        <StatGrid>
          <StatCard
            emphasis
            label={strings.statWords}
            value={fmt(counts.words)}
          />
          <StatCard label={strings.statFindings} value={fmt(counts.findings)} />
          <StatCard
            label={strings.statComparisons}
            value={fmt(counts.comparisons)}
          />
          <StatCard
            label={strings.statLimitations}
            value={fmt(counts.limitations)}
          />
        </StatGrid>
        <CheckList
          items={checks}
          label={strings.checksLabel}
          stateLabels={strings.checkStates}
        />
      </section>

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="ds"
        labels={documentLabels}
      />
    </div>
  );
}
